import {register} from '../vendor/theme-scripts/theme-sections';
import debounce from '../util/debounce';
import {slider} from '../features/slider';
import parallaxHero from '../features/parallax-hero';
import productGridReviews from '../features/product-grid';
import {popoutSection} from '../features/popout';
import hideElement from '../util/hide-element';
import {swatchGridSection} from '../features/swatch';
import {quickAddProduct} from '../features/quick-add';

var selectors = {
  sort: '[data-sort-enabled]',
  sortLinks: '[data-sort-link]',
  sortValue: 'data-value',
  collectionNav: '[data-collection-nav]',
  collectionNavGrouped: '[data-collection-nav-grouped]',
  collectionSidebarHeading: '[data-collection-sidebar-heading]',
  collectionSidebar: '[data-collection-sidebar]',
  collectionSidebarSlider: '[data-collection-sidebar-slider]',
  collectionSidebarSlideOut: '[data-collection-sidebar-slide-out]',
  collectionSidebarCloseButton: '[data-collection-sidebar-close]',
  groupTagsButton: '[data-aria-toggle]',
  headerSticky: '[data-header-sticky="sticky"]',
  headerHeight: '[data-header-height]',
  linkAdd: '[data-link-add]',
  linkRemove: '[data-link-remove]',
};

var classes = {
  expanded: 'expanded',
  expanding: 'expanding',
  noMobileAnimation: 'no-mobile-animation',
};

let sections = {};
class Collection {
  constructor(section) {
    this.container = section.container;
    this.sort = this.container.querySelector(selectors.sort);
    this.collectionNav = this.container.querySelector(selectors.collectionNav);
    this.sortLinks = this.container.querySelectorAll(selectors.sortLinks);
    this.collectionSidebar = this.container.querySelector(selectors.collectionSidebar);
    this.collectionSidebarCloseButton = this.container.querySelector(selectors.collectionSidebarCloseButton);
    this.groupTagsButton = this.container.querySelector(selectors.groupTagsButton);
    this.headerSticky = document.querySelector(selectors.headerSticky) !== null;
    this.resizeEvent = debounce(() => this.hideSidebarSliderOnMobile(), 200);

    this.groupTagsButtonClickEvent = (evt) => this.groupTagsButtonClick(evt);
    this.groupTagsToggleFocusoutEvent = (evt) => this.groupTagsToggleFocusout(evt);
    this.collectionSidebarCloseEvent = (evt) => this.collectionSidebarClose(evt);

    this.init();
  }

  init() {
    if (this.sort) {
      this.initClick();
    }
    this.removeUnusableFilters();

    if (this.groupTagsButton !== null) {
      this.hideSidebarSliderOnMobile();

      this.groupTagsButton.addEventListener('click', this.groupTagsButtonClickEvent);
      this.groupTagsButton.addEventListener('focusout', this.groupTagsToggleFocusoutEvent);

      document.addEventListener('theme:resize', this.resizeEvent);

      // Prevent filters closing animation on page load
      setTimeout(() => {
        this.collectionSidebar.classList.remove(classes.noMobileAnimation);
      }, 1000);
    }

    if (this.collectionSidebarCloseButton !== null) {
      this.collectionSidebarCloseButton.addEventListener('click', this.collectionSidebarCloseEvent);
    }

    // Hide filters sidebar on ESC keypress
    this.container.addEventListener(
      'keyup',
      function (evt) {
        if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
          return;
        }
        this.hideSidebar();
      }.bind(this)
    );
  }

  onClick(e) {
    const sort = e.currentTarget.getAttribute(selectors.sortValue);
    const url = new window.URL(window.location.href);
    const params = url.searchParams;
    params.set('sort_by', sort);
    url.search = params.toString();
    window.location.replace(url.toString());
  }

  initClick() {
    this.sortLinks.forEach((link) => {
      link.addEventListener(
        'click',
        function (e) {
          this.onClick(e);
        }.bind(this)
      );
    });
  }

  removeUnusableFilters() {
    const collectionNavGrouped = this.container.querySelectorAll(selectors.collectionNavGrouped);
    if (collectionNavGrouped.length > 0) {
      collectionNavGrouped.forEach((element) => {
        const linkAdd = element.querySelector(selectors.linkAdd);
        const linkRemove = element.querySelector(selectors.linkRemove);

        if (!linkAdd && !linkRemove) {
          hideElement(element);
          hideElement(element.parentElement.querySelector(selectors.collectionSidebarHeading));
        }
      });
    }
  }

  showSidebarCallback() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const collectionSidebarSlideOut = this.container.querySelector(selectors.collectionSidebarSlideOut);
    const collectionSidebarSlider = this.container.querySelector(selectors.collectionSidebarSlider);
    const scrollableElement = collectionSidebarSlider || collectionSidebarSlideOut;

    if (windowWidth < theme.sizes.small && collectionSidebarSlideOut === null) {
      const headerHeight = this.headerSticky ? document.querySelector(selectors.headerHeight).getBoundingClientRect().height : 0;
      const buttonTopPosition = this.groupTagsButton.offsetTop - headerHeight;

      window.scrollTo({
        top: buttonTopPosition,
        left: 0,
        behavior: 'smooth',
      });
    }

    if (windowWidth < theme.sizes.small || collectionSidebarSlideOut !== null) {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: scrollableElement}));
    }
  }

  hideSidebar() {
    this.groupTagsButton.setAttribute('aria-expanded', 'false');
    this.collectionSidebar.classList.add(classes.expanding);
    this.collectionSidebar.classList.remove(classes.expanded);

    setTimeout(() => {
      this.collectionSidebar.classList.remove(classes.expanding);
    }, 500);

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
  }

  hideSidebarSliderOnMobile() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (windowWidth < theme.sizes.small) {
      this.hideSidebar();
    }
  }

  collectionSidebarClose(evt) {
    evt.preventDefault();
    this.hideSidebar();
  }

  groupTagsToggleFocusout(evt) {
    const popoutLostFocus = this.collectionSidebar.contains(evt.relatedTarget);

    if (!popoutLostFocus) {
      this.hideSidebar();
    }
  }

  groupTagsButtonClick() {
    if (this.collectionSidebar.classList.contains(classes.expanded)) {
      this.showSidebarCallback();
    } else {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }
  }

  onUnload() {
    if (this.groupTagsButton !== null) {
      this.groupTagsButton.removeEventListener('click', this.groupTagsButtonClickEvent);
      this.groupTagsButton.removeEventListener('focusout', this.groupTagsToggleFocusoutEvent);
    }

    if (this.collectionSidebarCloseButton !== null) {
      this.collectionSidebarCloseButton.removeEventListener('click', this.collectionSidebarCloseEvent);
    }
  }
}

const collectionSection = {
  onLoad() {
    sections[this.id] = new Collection(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
};

register('collection', [slider, parallaxHero, productGridReviews, quickAddProduct, collectionSection, popoutSection, swatchGridSection]);
